<template>
  <div>
    <!-- TKP-94 md.15: hiçbir hukuki metnin kapsamına girmemiş süreçler ayrı gösterilir -->
    <div class="mb-3">
      <b-button size="sm" variant="light" data-test="uncovered-toggle" @click="toggleUncovered">
        <i class="bx bx-list-ul align-middle mr-1"></i>
        {{ $t("legal-document.uncovered-show") }}
        <b-badge v-if="uncovered.list.length" variant="warning" class="ml-1">{{ uncovered.list.length }}</b-badge>
      </b-button>
      <b-collapse v-model="uncovered.open" class="mt-2">
        <div class="card card-body" data-test="uncovered-panel">
          <h6>{{ $t("legal-document.uncovered-title") }}</h6>
          <small class="d-block text-muted mb-2">{{ $t("legal-document.uncovered-hint") }}</small>
          <content-loading v-if="uncovered.loading" />
          <div v-else-if="uncovered.failed" class="alert alert-danger mb-0" data-test="uncovered-failed">{{ $t("legal-document.uncovered-failed") }}</div>
          <div v-else-if="!uncovered.list.length" class="text-muted" data-test="uncovered-empty">{{ $t("legal-document.uncovered-empty") }}</div>
          <ul v-else class="mb-0" data-test="uncovered-list">
            <li v-for="p in uncovered.list" :key="p._id">
              {{ p.name | translate }}
              <small v-if="p.department" class="text-muted">— {{ p.department.name | translate }}</small>
            </li>
          </ul>
        </div>
      </b-collapse>
    </div>

    <!-- TKP-93 md.12: yayımlanan metin ile güncel envanterden üretilecek metin yan yana -->
    <b-modal id="legal-document-diff" size="xl" :title="$t('legal-document.diff-title')" hide-footer>
      <content-loading v-if="diff.loading" />
      <LegalDocumentDiff v-else :stored="diff.stored" :current="diff.current" :has-data="diff.hasData" />
    </b-modal>

    <table class="table table-bordered">
      <thead class="bg-dark text-white">
        <tr>
          <th class="text-center">
            <div>{{ $t("common.language") }} <LanguageFilter indent="template" :request="request" @changed="languageFilterChanged" /></div>
          </th>
          <th class="align-middle">{{ $t("common.version") }}</th>
          <th class="align-middle">{{ $t("common.created-by") }}</th>
          <th class="align-middle">{{ $t("common.at") }}</th>
          <!-- TKP-94 md.14: her versiyonda oluşturan, ONAYLAYAN ve tarih -->
          <th class="align-middle">{{ $t("legal-document.approved-by") }}</th>
          <th class="align-middle">{{ $t("legal-document.approved-at") }}</th>
          <th class="align-middle">{{ $t("legal-document.name") }}</th>
          <th class="align-middle">{{ $t("legal-document-template.text") }}</th>
          <th class="align-middle">{{ $t("file-manager.file") }}</th>
          <th class="align-middle">
            <a href="javascript:;" @click="toggleFilter">
              <span v-if="filterEnabled" class="text-warning">
                <i class="bx bxs-filter-alt"></i>
                {{ $t("common.filters-hide") }}
              </span>
              <span v-else class="text-white">
                <i class="bx bxs-filter-alt"></i>
                {{ $t("common.filters-show") }}
              </span>
            </a>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="filterEnabled">
          <th></th>
          <th><input class="form-control" v-model.number="versionFilter" /></th>
          <th></th>
          <th></th>
          <th></th>
          <th></th>
          <th><input class="form-control" v-model="nameFilter" /></th>
          <th></th>
        </tr>
        <template v-if="pagination">
          <tr v-for="item in pagination.docs" :key="item._id">
            <td>
              <language-badge v-if="item.template" :langCode="item.template.language" />
            </td>
            <td>{{ item.version }}</td>
            <td>
              <span v-if="item.createdBy">{{ item.createdBy.name }}</span>
            </td>
            <td>{{ item.createdAt | date(true) }}</td>
            <!-- damgasız eski kayıtlarda (TKP-92 öncesi onaylananlar) "—" -->
            <td data-test="approved-by">{{ item.approvedBy ? item.approvedBy.name : "—" }}</td>
            <td data-test="approved-at">{{ item.approvedAt ? $options.filters.date(item.approvedAt, true) : "—" }}</td>
            <td>
              {{ item.name | translate }}
              <!-- TKP-93 md.10–11: metin üretildikten sonra envanter değiştiyse uyarı; damgasız eski metinler işaretlenmez -->
              <b-badge v-if="isStale(item)" variant="warning" class="ml-2" data-test="stale-badge" v-b-tooltip.hover :title="staleTooltip(item)">
                {{ $t("legal-document.stale") }}
              </b-badge>
            </td>
            <td>
              <span v-if="item.template">
                {{ item.template.name | translate }}
              </span>
            </td>
            <td>
              <span v-if="item.file" @click="downloadFile(item.file)" v-b-tooltip.hover :title="$t('common.show')" class="hand">
                <i class="bx bx-search align-middle mr-1"></i>
                {{ item.file.name | translate }}
              </span>
              <span v-else>
                <i>({{ $t("file-manager.not-found") }})</i>
              </span>
            </td>

            <td>
              <!-- TKP-93 md.12: farkları gör / güncel envantere uyarla — yalnız bayat ve kriter taşıyan metinde -->
              <template v-if="isStale(item)">
                <i
                  v-if="canRefresh(item)"
                  class="bx bx-git-compare text-primary hand mr-3 font-size-14"
                  data-test="diff-open"
                  v-b-tooltip.hover
                  :title="$t('legal-document.diff')"
                  @click="openDiff(item)"
                ></i>
                <i
                  v-if="canRefresh(item)"
                  class="bx bx-refresh text-success hand mr-3 font-size-16"
                  data-test="refresh"
                  v-b-tooltip.hover
                  :title="$t('legal-document.refresh')"
                  @click="refresh(item)"
                ></i>
                <i v-else class="bx bx-info-circle text-muted mr-3 font-size-14" data-test="no-criteria" v-b-tooltip.hover :title="$t('legal-document.no-criteria')"></i>
              </template>
              <!-- TKP-94 md.15: silme yalnız hukuk yöneticisinde; sunucu da aynı kuralı uyguluyor -->
              <i
                v-if="canDelete"
                class="bx bx-trash text-danger hand mr-3 font-size-14"
                data-test="delete"
                v-b-tooltip.hover
                :title="$t('legal-document.delete')"
                @click="deleteById(item._id)"
              ></i>
            </td></tr
        ></template>
      </tbody>
    </table>
    <content-loading v-if="isLoading" />
    <empty v-if="!isLoading && (!pagination || pagination.docs.length == 0)" width="200px">
      <h4 class="text-center">{{ $t("common.no-data") }}</h4>
    </empty>

    <!-- Begin: Pagination -->

    <div v-if="pagination" class="row">
      <div class="col">
        <ul class="pagination pagination-rounded mb-0">
          <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="10" size="sm"></b-pagination>
        </ul>
      </div>
    </div>
    <!-- End: Pagination -->
  </div>
</template>

<script>
import LanguageBadge from "@c/language-badge";
import LanguageFilter from "@c/language-filter";
import LibraryItems from "@cc/library-items";
import LegalDocumentDiff from "./component/legal-document-diff";

import { canRefresh, contentOf, criteriaToSelection, isStale, staleHint } from "./document-freshness";

const state = "organizationLegalDocument";
export default {
  props: ["product"],
  components: { LibraryItems, LanguageBadge, LanguageFilter, LegalDocumentDiff },
  data() {
    return {
      nameFilter: undefined,
      versionFilter: undefined,
      filterEnabled: false,
      request: {
        options: { page: 1, limit: 15, sort: { name: "asc" } },
        query: { approved: { value: true, isObject: true } },
      },
      diff: { loading: false, stored: "", current: "", hasData: null },
      uncovered: { open: false, loading: false, list: [], failed: false },
    };
  },
  computed: {
    pagination() {
      return this.$store.getters[`${state}/pagination`];
    },
    isLoading() {
      return this.$store.getters[`${state}/isLoading`];
    },
    /** TKP-94 md.15: metin silme yalnız hukuk yöneticisinde (sunucu kuralıyla aynı liste). */
    canDelete() {
      return this.currentAccountCan("LEGAL_DOCUMENT_VIEW__CAN_DELETE");
    },
    hasApproveOrRejectPermission() {
      if (this.currentAccountCan("LEGAL_DOCUMENT_LEGAL_DOCUMENT_VIEW__HAS_APPROVE_OR_REJECT_PERMISSION")) {
        return true;
      }

      return false;
    },
  },
  watch: {
    request: {
      deep: true,
      handler() {
        this.debounce(() => this.paginate());
      },
    },
    nameFilter(n, o) {
      this.request.query = {
        ...this.request.query,
        name: { value: n },
      };
    },
    versionFilter(n, o) {
      if (n && n != "" && n != " ") {
        this.request.query = {
          ...this.request.query,
          version: { value: Number(n), isObject: true },
        };
      } else {
        this.request.query["version"] = null;
        delete this.request.query["version"];
      }
    },
  },
  mounted() {
    if (this.product) {
      this.request = { ...this.request, query: { ...this.request.query, products: { value: this.product, isObject: true } } };
    }
  },
  methods: {
    isStale,
    canRefresh,
    /** Rozetin ipucu: kaç envanter değişikliği geride. */
    staleTooltip(item) {
      const hint = staleHint(item);
      return hint ? this.$t("legal-document.stale-hint", { behind: hint.behind }) : this.$t("legal-document.stale");
    },
    /**
     * TKP-93 md.12: saklanan içerik ile AYNI kriterlerle bugün üretilecek içerik yan yana.
     * Liste ucu ONAYLI metinlerde `content` alanını düşürüyor (dosyası var), o yüzden metin ayrıca çekilir.
     */
    async openDiff(item) {
      this.diff = { loading: true, stored: contentOf(item), current: "", hasData: null };
      this.$bvModal.show("legal-document-diff");
      try {
        const detail = await this.$store.dispatch(`${state}/findById`, item._id).catch(() => null);
        const stored = contentOf(detail?.data ?? item) || contentOf(item);
        const { data } = await this.$store.dispatch(`${state}Template/preview`, { template: item.template._id, criteria: criteriaToSelection(item.criteria) });
        this.diff = { loading: false, stored, current: (data?.content || []).join(""), hasData: data?.hasData ?? null };
      } catch (error) {
        this.diff = { ...this.diff, loading: false };
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    /** TKP-93 md.12: metni güncel envanterden yeniden üret — onay kuralları (TKP-92) sunucuda işler. */
    async refresh(item) {
      if (!(await this.confirm(this.$t("legal-document.refresh-confirm")))) return;
      try {
        const { data } = await this.$store.dispatch(`${state}Template/generate`, { template: item.template._id, criteria: criteriaToSelection(item.criteria) });
        this.$toast.success(this.$t(data.approved ? "legal-document.refreshed" : "legal-document.refresh-sent", { count: data.created }));
        this.paginate();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    /** TKP-94 md.15: kapsam dışı süreçler — açılırken bir kez çekilir. */
    async toggleUncovered() {
      this.uncovered.open = !this.uncovered.open;
      if (!this.uncovered.open) return;
      this.uncovered.loading = true;
      this.uncovered.failed = false;
      try {
        const list = await this.$store.dispatch(`${state}/uncoveredProcesses`);
        this.uncovered.list = Array.isArray(list) ? list : [];
      } catch (error) {
        // hata ile "hepsi kapsamda" AYNI görünmemeli
        this.uncovered.failed = true;
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.uncovered.loading = false;
      }
    },
    toggleFilter() {
      this.filterEnabled = !this.filterEnabled;
    },
    async paginate() {
      await this.$store.dispatch(`${state}/paginate`, this.request);
    },
    languageFilterChanged(e) {
      this.request = e;
    },

    downloadFile(file) {
      // this.$store.commit("organizationFileCategory/selectedParentCategory", file.category);
      if (file.isPeriodical && file.approval?.path) {
        this.$store.commit("fileManager/selectedTab", 1);
        this.$router.push(`/${this.currentProduct ? this.currentProduct.toLowerCase() : "organization"}/file-approval-survey/${file._id}/survey`);
        return;
      }

      const url = this.currentProduct ? `/${this.currentProduct}/file-manager` : "/organization/file-manager";
      this.$router.push({ path: url, query: { id: file._id, ...(file.historyId ? { historyId: file.historyId } : {}) } });
      //  this.$store.commit("fileManager/addDownloadedFile", {file});
    },
    async deleteById(id) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`${state}/delete`, id);
        this.paginate();
        this.$toast.success(this.$t("common.delete-successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
